import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageTitle from "@/components/base/PageTitle";
import Testimonials from "./components/Testimonials";
import WhyLabDiamonds from "./components/WhyLabDiamonds";

const videoItems = [
  {
    label: "CURATED DIAMOND SELECTIONS",
    labelAlign: "left",
    img: "https://readdy.ai/api/search-image?query=loose%20diamonds%20scattered%20on%20dark%20black%20velvet%20surface%2C%20multiple%20brilliant%20cut%20diamonds%2C%20luxury%20jewelry%20photography%2C%20dramatic%20lighting%2C%20high%20contrast%2C%20professional%20studio%20shot%2C%20ultra%20detailed%20gemstones&width=400&height=320&seq=home-vid-1&orientation=landscape",
    video: "https://videos.pexels.com/video_files/3209828/3209828-hd.mp4",
  },
  {
    label: "",
    labelAlign: "center",
    img: "https://readdy.ai/api/search-image?query=oval%20cut%20diamond%20engagement%20ring%20and%20emerald%20cut%20diamond%20ring%20side%20by%20side%20on%20dark%20black%20background%2C%20luxury%20fine%20jewelry%2C%20gold%20setting%2C%20brilliant%20sparkle%2C%20professional%20photography&width=400&height=320&seq=home-vid-2&orientation=landscape",
    video: "https://assets.mixkit.co/videos/preview/mixkit-diamond-engagement-ring-between-floorboards-4288-large.mp4",
  },
  {
    label: "GLOBALLY SOURCED. PERSONALLY CURATED.",
    labelAlign: "right",
    img: "https://readdy.ai/api/search-image?query=three%20diamond%20eternity%20bands%20stacked%20together%20on%20dark%20black%20surface%2C%20gold%20rings%20with%20brilliant%20diamonds%2C%20luxury%20jewelry%20photography%2C%20elegant%20and%20refined%2C%20professional%20studio%20lighting&width=400&height=320&seq=home-vid-3&orientation=landscape",
    video: "https://assets.mixkit.co/videos/preview/mixkit-wedding-rings-in-a-case-4290-large.mp4",
  },
];

export default function Home() {
  const videoRef = useScrollReveal<HTMLDivElement>();

  return (
    <div className="min-h-screen bg-zayan-bg flex flex-col">
      <PageTitle title="Zayan Atelier" />
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=stunning%20diamond%20solitaire%20engagement%20ring%20with%20brilliant%20round%20cut%20diamond%20in%20gold%20prong%20setting%2C%20dark%20dramatic%20black%20background%2C%20luxury%20jewelry%20photography%2C%20extreme%20close%20up%2C%20sparkling%20facets%2C%20ultra%20high%20detail&width=1400&height=900&seq=home-hero-bg&orientation=landscape"
            alt="Diamond ring hero"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zayan-bg via-zayan-bg/80 to-zayan-bg/20" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full px-6 lg:px-16 pt-[130px] md:pt-[170px] pb-16">
          <div className="max-w-2xl">
            <p className="text-zayan-gold text-2xl tracking-[0.25em] mb-3 font-sans uppercase">
              Zayan Atelier
            </p>
            <h1 className="font-serif text-5xl lg:text-7xl text-zayan-gold leading-tight mb-6">
              Direct Sourcing of IGI-Certified Lab Diamonds.
            </h1>
            <p className="text-zayan-textMuted text-sm lg:text-base leading-relaxed mb-10 max-w-md">
              Zayan Atelier provides direct access to IGI-certified lab-grown diamonds through trusted international sourcing networks, offering refined selections, personalized guidance, and exceptional value.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link
                to="/request-a-diamond"
                className="border border-zayan-gold bg-zayan-gold/10 text-zayan-gold px-8 py-3 text-xs tracking-[0.15em] hover:bg-zayan-gold hover:text-zayan-bg transition-colors text-center whitespace-nowrap"
              >
                BEGIN YOUR SEARCH
              </Link>
              <Link
                to="/contact"
                className="border border-zayan-gold/50 text-zayan-textMuted px-8 py-3 text-xs tracking-[0.15em] hover:border-zayan-gold hover:text-zayan-gold transition-colors text-center whitespace-nowrap"
              >
                SCHEDULE CONSULTATION
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-zayan-textDarker text-xs tracking-[0.15em]">
              <span>IGI-CERTIFIED</span>
              <span className="text-zayan-gold">•</span>
              <span>PERSONALIZED SOURCING</span>
              <span className="text-zayan-gold">•</span>
              <span>GLOBAL ACCESS</span>
            </div>
          </div>
        </div>
      </section>

      {/* Video Gallery Section */}
      <section className="bg-zayan-bg border-t border-zayan-border">
        <div className="grid grid-cols-1 md:grid-cols-3 reveal" ref={videoRef}>
          {videoItems.map((item, i) => (
            <div key={i} className={`relative group overflow-hidden cursor-pointer`}>
              {/* Label */}
              {item.label && (
                <div
                  className={`absolute top-4 z-10 px-4 text-xs tracking-[0.15em] text-zayan-gold font-sans ${
                    item.labelAlign === "right"
                      ? "right-4 text-right"
                      : "left-4 text-left"
                  }`}
                >
                  {item.label}
                  <div className="mt-1 h-px w-12 bg-zayan-gold/50 ml-auto" />
                </div>
              )}
              <div className="w-full h-64 lg:h-80 overflow-hidden">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster={item.img}
                  aria-label={item.label || "Diamond collection video"}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                >
                  <source src={item.video} type="video/mp4" />
                </video>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Testimonials />

      <WhyLabDiamonds />

      <Footer />
    </div>
  );
}